export type SubmittalLogView = "default" | "voided" | "deleted";

function stripTrailingSlashes(path: string): string {
  let end = path.length;
  while (end > 0 && path.charCodeAt(end - 1) === 47) end -= 1;
  return end === path.length ? path : path.slice(0, end);
}

export const SUBMITTAL_LOG_VIEW_TITLES: Record<SubmittalLogView, string> = {
  default: "Submittals",
  voided: "Voided Submittals",
  deleted: "Recycle Bin"
};

/**
 * Resolves voided/deleted vs default for SubmittalLog routes only.
 * Untracked (`/submittals-untracked`) uses a separate page and returns `"default"`.
 */
export function getSubmittalLogViewFromPath(
  pathname: string
): SubmittalLogView {
  const normalizedPath = stripTrailingSlashes(pathname);
  if (
    normalizedPath.endsWith("/submittals-voided") ||
    normalizedPath.includes("/submittals-voided/")
  ) {
    return "voided";
  }
  if (
    normalizedPath.endsWith("/submittals-deleted") ||
    normalizedPath.includes("/submittals-deleted/")
  ) {
    return "deleted";
  }
  return "default";
}

/** Saved column views apply only to the main Submittal Log (not voided/deleted). */
export function isSubmittalLogSavedViewsEnabled(
  logView: SubmittalLogView
): boolean {
  return logView === "default";
}

/** Voided and deleted logs share the Not Tracked header treatment (no add/import/views). */
export function isSubmittalLogRestrictedView(
  logView: SubmittalLogView
): boolean {
  return logView !== "default";
}

export type SubmittalLogHeaderIconFeature =
  | "SUBMITTALS"
  | "SUBMITTALS_VOIDED"
  | "SUBMITTALS_DELETED";

export function getSubmittalLogHeaderIconFeature(
  logView: SubmittalLogView
): SubmittalLogHeaderIconFeature {
  switch (logView) {
    case "voided":
      return "SUBMITTALS_VOIDED";
    case "deleted":
      return "SUBMITTALS_DELETED";
    default:
      return "SUBMITTALS";
  }
}

export type SubmittalDetailsHeaderIconFeature =
  | SubmittalLogHeaderIconFeature
  | "SUBMITTALS_UNTRACKED";

/** Icon for submittal details header based on void/deleted/tracked state. */
export function getSubmittalDetailsHeaderIconFeature({
  deletedFromSource,
  isVoid,
  isTracked
}: {
  deletedFromSource?: boolean | null;
  isVoid?: boolean | null;
  isTracked?: boolean | null;
}): SubmittalDetailsHeaderIconFeature {
  if (deletedFromSource) return "SUBMITTALS_DELETED";
  if (isVoid) return "SUBMITTALS_VOIDED";
  return isTracked ? "SUBMITTALS" : "SUBMITTALS_UNTRACKED";
}

export function getSubmittalLogRoute(
  projectId: string,
  view: SubmittalLogView
): string {
  switch (view) {
    case "voided":
      return `/project/${projectId}/submittals-voided`;
    case "deleted":
      return `/project/${projectId}/submittals-deleted`;
    default:
      return `/project/${projectId}/submittals`;
  }
}

export function getSubmittalLogUntrackedRoute(projectId: string): string {
  return `/project/${projectId}/submittals-untracked`;
}

export function isValidSubmittalLogReturnTo(
  projectId: string,
  returnTo: string
): boolean {
  const normalized = stripTrailingSlashes(returnTo);
  const allowedRoutes = [
    getSubmittalLogRoute(projectId, "default"),
    getSubmittalLogRoute(projectId, "voided"),
    getSubmittalLogRoute(projectId, "deleted"),
    getSubmittalLogUntrackedRoute(projectId)
  ].map((route) => stripTrailingSlashes(route));

  return allowedRoutes.includes(normalized);
}

export type SubmittalLogLocationState = {
  submittalLogReturnTo?: string;
};

export function getSubmittalLogReturnToFromLocation(
  state: unknown
): string | undefined {
  if (!state || typeof state !== "object") return undefined;
  const route = (state as SubmittalLogLocationState).submittalLogReturnTo;
  return typeof route === "string" ? route : undefined;
}

export function getValidatedSubmittalLogReturnTo(
  projectId: string,
  state: unknown
): string | undefined {
  const returnTo = getSubmittalLogReturnToFromLocation(state);
  if (!returnTo || !isValidSubmittalLogReturnTo(projectId, returnTo)) {
    return undefined;
  }
  return stripTrailingSlashes(returnTo) || returnTo;
}

export function getSubmittalDetailsLinkTo(
  projectId: string,
  submittalId: string,
  submittalLogReturnTo?: string
) {
  const validatedReturnTo =
    submittalLogReturnTo &&
    isValidSubmittalLogReturnTo(projectId, submittalLogReturnTo)
      ? stripTrailingSlashes(submittalLogReturnTo) || submittalLogReturnTo
      : undefined;

  return {
    pathname: `/project/${projectId}/submittals/${submittalId}`,
    ...(validatedReturnTo
      ? {
          state: {
            submittalLogReturnTo: validatedReturnTo
          } satisfies SubmittalLogLocationState
        }
      : {})
  };
}
